import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type PointerEvent, type ReactNode } from 'react';
import { Button, Checkbox, InlineNotification, Select, SelectItem, TextInput } from '@carbon/react';
import { Filter } from '@carbon/icons-react';
import { vnDayKey } from '@ruby-cms/shared';
import { useUrlFilters, ExportButton, type UrlFilters } from './Filters';
import { Money, compactVnd } from './format';
import { GRAINS, GrainSwitch, periodDefaultFrom, periodLabel, type Grain } from './Analytics';

// Shared pieces of the four report screens (P1-D16): the filter row (Vietnam-day range with presets,
// agent with its tree, real depositors only, plus a screen's own selects), KPI tiles with the three
// comparisons, and the charts. Chart colours: one validated pair for the dark surface (dataviz
// validate_palette.js, dark, surface layer-01): series 1 ruby, series 2 blue. Single-series charts
// use series 1 and no legend; two series always carry a legend.

export type Win = { from: string; to: string };
export type Cmp<T> = { prevDay: T; prevWeek: T; mtd: T; lastMtd: T };
export type Scope = { agent: string | null; depositors: boolean; players: number | null };
export type Kind = 'money' | 'count' | 'pct' | 'sec' | 'num';

const shift = (d: string, n: number) => new Date(new Date(d + 'T00:00:00Z').getTime() + n * 86_400_000).toISOString().slice(0, 10);
export const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
export const SERIES = ['var(--series-1)', 'var(--series-2)'] as const;

export function useInsightFilters(extra: readonly string[] = []): UrlFilters {
  const today = vnDayKey(new Date());
  return useUrlFilters(['from', 'to', 'agent', 'depositors', 'grain', ...extra], { defaults: { from: today, to: today } });
}

/** How the screen groups its series: by day (the default), week or month. */
export const grainOf = (f: UrlFilters): Grain => ((GRAINS as readonly string[]).includes(f.values.grain ?? '') ? (f.values.grain as Grain) : 'day');
/** Row label of a period: 29/09 for a day, Tuần 22/09/2026 or Tháng 09/2026 with * when the range cuts it or it is still running. */
export const periodRow = (p: string, g: Grain, partial?: boolean) => (g === 'day' ? ddmm(p) : periodLabel(p, g) + (partial ? '*' : ''));

function presets(today: string): [string, Win][] {
  return [
    ['Hôm nay', { from: today, to: today }],
    ['Hôm qua', { from: shift(today, -1), to: shift(today, -1) }],
    ['7 ngày', { from: shift(today, -6), to: today }],
    ['30 ngày', { from: shift(today, -29), to: today }],
    ['Tháng này', { from: today.slice(0, 8) + '01', to: today }],
  ];
}

export type SelectSpec = { key: string; label: string; options: readonly (readonly [string, string])[] };

// Dates, agent and the checkbox apply on "Xem"; a preset or a select applies at once.
export function InsightBar({ id, f, selects = [], exportPath, exportSections }: {
  id: string; f: UrlFilters; selects?: SelectSpec[]; exportPath: string; exportSections: readonly (readonly [string, string])[];
}) {
  const today = vnDayKey(new Date());
  const [draft, setDraft] = useState(f.values);
  const [section, setSection] = useState(exportSections[0]?.[0] ?? '');
  const applied = JSON.stringify(f.values);
  useEffect(() => { setDraft(f.values); }, [applied]);
  const submit = (e: FormEvent) => { e.preventDefault(); f.set(draft); };
  const v = f.values;
  // a week or month view opens on the last 12 weeks / months, a day view on the last 30 days
  const pick = (g: Grain) => { const to = draft.to || today; const next = { ...draft, grain: g === 'day' ? '' : g, from: periodDefaultFrom(to, g), to }; setDraft(next); f.set(next); };
  return (
    <>
    <GrainSwitch value={grainOf(f)} onChange={pick} />
    <form className="toolbar-filters insight-bar" onSubmit={submit} aria-label="Bộ lọc báo cáo">
      <TextInput id={`${id}-from`} type="date" labelText="Từ ngày" value={draft.from ?? ''} max={draft.to || today} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
      <TextInput id={`${id}-to`} type="date" labelText="Đến ngày" value={draft.to ?? ''} min={draft.from} max={today} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
      <TextInput id={`${id}-agent`} labelText="Đại lý (mã, gồm cả cây)" placeholder="Tất cả" value={draft.agent ?? ''} onChange={(e) => setDraft({ ...draft, agent: e.target.value.trim() })} />
      {selects.map((s) => (
        <Select key={s.key} id={`${id}-${s.key}`} labelText={s.label} value={draft[s.key] ?? ''}
          onChange={(e) => { const next = { ...draft, [s.key]: e.target.value }; setDraft(next); f.set(next); }}>
          {s.options.map(([val, t]) => <SelectItem key={val} value={val} text={t} />)}
        </Select>
      ))}
      <div className="insight-bar__check">
        <Checkbox id={`${id}-dep`} labelText="Chỉ người đã nạp tiền" checked={draft.depositors === '1'}
          onChange={(_e, { checked }) => { const next = { ...draft, depositors: checked ? '1' : '' }; setDraft(next); f.set(next); }} />
      </div>
      <Button type="submit" kind="tertiary" size="md" renderIcon={Filter}>Xem</Button>
      {f.active ? <Button kind="ghost" size="md" onClick={() => f.clear()}>Bỏ lọc</Button> : null}
      <div className="row presets insight-bar__presets">
        {presets(today).map(([l, r]) => (
          <Button key={l} kind={r.from === v.from && r.to === v.to ? 'secondary' : 'ghost'} size="sm"
            onClick={() => { const next = { ...draft, ...r }; setDraft(next); f.set(next); }}>{l}</Button>
        ))}
      </div>
      <span className="toolbar-filters__grow" />
      <div className="insight-bar__export">
        {exportSections.length > 1 ? (
          <Select id={`${id}-section`} labelText="Bảng xuất" size="md" value={section} onChange={(e) => setSection(e.target.value)}>
            {exportSections.map(([val, t]) => <SelectItem key={val} value={val} text={t} />)}
          </Select>
        ) : null}
        <ExportButton path={exportPath} query={{ ...f.query, section }} />
      </div>
    </form>
    </>
  );
}

export function ScopeNote({ scope }: { scope: Scope | undefined }) {
  if (!scope || (!scope.agent && !scope.depositors)) return null;
  const parts = [scope.agent ? `đại lý ${scope.agent} và cả cây` : '', scope.depositors ? 'chỉ người đã nạp tiền' : ''].filter(Boolean);
  return <p className="muted note insight-scope">Đang lọc: {parts.join(', ')} ({(scope.players ?? 0).toLocaleString('vi-VN')} tài khoản).</p>;
}

export function Alerts({ items }: { items: { key: string; title: string; body: string }[] }) {
  if (!items.length) return null;
  return (
    <div className="insight-alerts" role="region" aria-label="Cảnh báo">
      {items.map((a) => <InlineNotification key={a.key} kind="warning" lowContrast hideCloseButton title={a.title} subtitle={a.body} />)}
    </div>
  );
}

// ---------------------------------------------------------------- values and comparisons

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

export function fmt(v: unknown, kind: Kind): string {
  const n = num(v);
  if (n === null || Number.isNaN(n)) return '-';
  switch (kind) {
    case 'money': return compactVnd(BigInt(Math.round(n)));
    case 'pct': return (n * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 }) + '%';
    case 'sec': return n < 60 ? `${Math.round(n)} giây` : n < 3600 ? `${(n / 60).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} phút` : `${(n / 3600).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} giờ`;
    case 'num': return n.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
    default: return Math.round(n).toLocaleString('vi-VN');
  }
}

export function Value({ v, kind }: { v: unknown; kind: Kind }) {
  if (kind === 'money') return v === null || v === undefined ? <span className="muted">-</span> : <Money v={String(v).split('.')[0]!} unit />;
  return <span className="num">{fmt(v, kind)}</span>;
}

// change of a against b: a ratio for amounts and counts, percentage points for a percentage
function change(a: unknown, b: unknown, kind: Kind): { text: string; dir: 1 | 0 | -1 } {
  const x = num(a), y = num(b);
  if (x === null || y === null) return { text: '-', dir: 0 };
  if (x === y) return { text: 'không đổi', dir: 0 };
  const dir = x > y ? 1 : -1;
  if (kind === 'pct') return { text: `${dir > 0 ? '+' : ''}${((x - y) * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} điểm`, dir };
  if (y === 0) return { text: `${dir > 0 ? '+' : '-'}${fmt(Math.abs(x - y), kind)} (kỳ trước 0)`, dir };
  const r = (x - y) / Math.abs(y);
  return { text: `${r > 0 ? '+' : ''}${(r * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`, dir };
}

function DeltaLine({ label, a, b, kind, title }: { label: string; a: unknown; b: unknown; kind: Kind; title: string }) {
  const c = change(a, b, kind);
  return (
    <li title={title}>
      <span className="insight-delta__label">{label}</span>
      <span className={'num insight-delta__v' + (c.dir > 0 ? ' is-up' : c.dir < 0 ? ' is-down' : '')}>
        {c.dir > 0 ? '▲ ' : c.dir < 0 ? '▼ ' : ''}{c.text}
      </span>
    </li>
  );
}

export function Deltas<T extends Record<string, unknown>>({ cur, cmp, k, kind, single }: { cur: T; cmp: Cmp<T>; k: keyof T & string; kind: Kind; single: boolean }) {
  return (
    <ul className="insight-delta">
      <DeltaLine label={single ? 'Hôm trước' : 'Lùi 1 ngày'} a={cur[k]} b={cmp.prevDay[k]} kind={kind} title={`Kỳ so sánh ${cmp.prevDay.from} đến ${cmp.prevDay.to}: ${fmt(cmp.prevDay[k], kind)}`} />
      <DeltaLine label={single ? 'Cùng thứ tuần trước' : 'Lùi 7 ngày'} a={cur[k]} b={cmp.prevWeek[k]} kind={kind} title={`Kỳ so sánh ${cmp.prevWeek.from} đến ${cmp.prevWeek.to}: ${fmt(cmp.prevWeek[k], kind)}`} />
      <DeltaLine label="Tháng này so cùng kỳ" a={cmp.mtd[k]} b={cmp.lastMtd[k]} kind={kind}
        title={`Từ đầu tháng ${fmt(cmp.mtd[k], kind)}; cùng kỳ tháng trước (${cmp.lastMtd.from} đến ${cmp.lastMtd.to}) ${fmt(cmp.lastMtd[k], kind)}`} />
    </ul>
  );
}

export function Tile<T extends Record<string, unknown>>({ label, k, kind, cur, cmp, hint, spark, sparkSpan, sparkDay, lead, warn }: {
  label: string; k: keyof T & string; kind: Kind; cur: T; cmp?: Cmp<T>; hint?: ReactNode;
  spark?: { day: string; v: number | null }[]; sparkSpan?: string; sparkDay?: (day: string) => string; lead?: boolean; warn?: boolean;
}) {
  const single = cur.from === cur.to;
  return (
    <div className={'kpi insight-tile' + (lead ? ' kpi--lead' : '') + (warn ? ' insight-tile--warn' : '')}>
      <span className="kpi__label">{label}</span>
      <span className="kpi__value"><Value v={cur[k]} kind={kind} /></span>
      {hint ? <span className="kpi__hint">{hint}</span> : null}
      {cmp ? <Deltas cur={cur} cmp={cmp} k={k} kind={kind} single={single} /> : null}
      {spark ? <Sparkline points={spark} kind={kind} label={label} spanLabel={sparkSpan} dayLabel={sparkDay} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- charts

function useWidth<E extends HTMLElement>(initial: number) {
  const ref = useRef<E>(null);
  const [w, setW] = useState(initial);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => { if (e && e.contentRect.width > 0) setW(Math.round(e.contentRect.width)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

// trend line under a tile (30 days, or 12 weeks / months): hover shows the period and value (a crosshair and a readout)
export function Sparkline({ points, kind, label, spanLabel = '30 ngày', dayLabel = ddmm }: {
  points: { day: string; v: number | null }[]; kind: Kind; label: string; spanLabel?: string; dayLabel?: (day: string) => string;
}) {
  const [ref, W] = useWidth<HTMLDivElement>(200);
  const [hover, setHover] = useState<number | null>(null);
  const H = 36, pad = 3;
  const vals = points.map((p) => p.v).filter((v): v is number => v !== null);
  if (vals.length < 2) return <div className="insight-spark insight-spark--empty muted">Chưa đủ số liệu {spanLabel}</div>;
  const max = Math.max(...vals), min = Math.min(...vals), span = max - min || 1;
  const x = (i: number) => pad + (i / Math.max(1, points.length - 1)) * (W - pad * 2);
  const y = (v: number) => pad + ((max - v) / span) * (H - pad * 2);
  let d = '';
  points.forEach((p, i) => { if (p.v === null) return; d += `${d && points[i - 1]?.v !== null ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`; });
  const h = hover !== null ? points[hover] : null;
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover(Math.max(0, Math.min(points.length - 1, Math.round(((e.clientX - r.left - pad) / (r.width - pad * 2)) * (points.length - 1)))));
  };
  return (
    <div className="insight-spark" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={`${label}, ${spanLabel}`} onPointerMove={move} onPointerLeave={() => setHover(null)}>
        {min < 0 && max > 0 ? <line x1={0} x2={W} y1={y(0)} y2={y(0)} className="chart__zero" /> : null}
        <path d={d} className="insight-spark__line" />
        {h && hover !== null && h.v !== null ? <>
          <line x1={x(hover)} x2={x(hover)} y1={0} y2={H} className="insight-crosshair" />
          <circle cx={x(hover)} cy={y(h.v)} r={4} className="insight-spark__dot" />
        </> : null}
      </svg>
      <div className="insight-spark__readout" aria-live="polite">{h ? <>{dayLabel(h.day)}: <span className="num">{fmt(h.v, kind)}</span></> : <span className="muted">{spanLabel}</span>}</div>
    </div>
  );
}

export type Series = { name: string; values: (number | null)[] };

// Columns for one or two series over labelled buckets (days, hours), grouped with a 2px gap; hover
// shows every series of the bucket. Negative values hang below the zero line.
export function Columns({ labels, series, kind, title, height = 220, hints }: {
  labels: string[]; series: Series[]; kind: Kind; title: string; height?: number; hints?: string[];
}) {
  const [ref, W] = useWidth<HTMLElement>(640);
  const [hover, setHover] = useState<number | null>(null);
  const clip = useId().replace(/:/g, '');
  const padL = W < 420 ? 44 : 60, padB = 22, padT = 10;
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const max = Math.max(0, ...all), min = Math.min(0, ...all), span = max - min || 1;
  const plotH = height - padB - padT;
  const y = (v: number) => padT + ((max - v) / span) * plotH;
  const step = (W - padL) / Math.max(1, labels.length);
  const group = Math.max(4, Math.min(36, step * 0.7));
  const bw = Math.max(2, (group - 2 * (series.length - 1)) / series.length);
  const ticks = [max, max / 2, 0, min].filter((v, i, a) => a.indexOf(v) === i && (v !== max / 2 || max > 0));
  return (
    <figure className="chart" ref={ref}>
      <div className="chart__readout" aria-live="polite">
        {hover !== null ? <>
          <span className="chart__readout-label">{hints?.[hover] ?? labels[hover]}</span>
          {series.map((s, i) => <span key={s.name} className="insight-readout__item"><i className="insight-key" style={{ background: SERIES[i] }} />{series.length > 1 ? s.name + ' ' : ''}<span className="num">{fmt(s.values[hover], kind)}</span></span>)}
        </> : <span className="muted">Rê chuột hoặc chạm vào một cột để xem số</span>}
      </div>
      {series.length > 1 ? (
        <ul className="insight-legend">{series.map((s, i) => <li key={s.name}><i className="insight-key" style={{ background: SERIES[i] }} />{s.name}</li>)}</ul>
      ) : null}
      <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height} role="img" aria-label={title} onPointerLeave={() => setHover(null)}>
        <defs><clipPath id={clip}><rect x={padL} y={0} width={W - padL} height={height} /></clipPath></defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart__zero' : 'chart__grid'} />
            <text x={padL - 8} y={y(t) + 4} className="chart__tick" textAnchor="end">{fmt(t, kind)}</text>
          </g>
        ))}
        <g clipPath={`url(#${clip})`}>
          {labels.map((l, i) => {
            const x0 = padL + step * i + (step - group) / 2;
            return (
              <g key={l + i} onPointerEnter={() => setHover(i)} onClick={() => setHover(i)}>
                <rect x={padL + step * i} y={padT} width={step} height={plotH} className="chart__hit" />
                {hover === i ? <rect x={padL + step * i} y={padT} width={step} height={plotH} className="insight-hover-band" /> : null}
                {series.map((s, j) => {
                  const v = s.values[i];
                  if (v === null || v === undefined || v === 0) return null;
                  const top = Math.min(y(v), y(0));
                  const h = Math.max(1, Math.abs(y(v) - y(0)));
                  const r = Math.min(4, bw / 2, h);
                  const bx = x0 + j * (bw + 2);
                  // rounded only at the data end, square on the baseline
                  const path = v > 0
                    ? `M${bx},${top + h}V${top + r}Q${bx},${top} ${bx + r},${top}H${bx + bw - r}Q${bx + bw},${top} ${bx + bw},${top + r}V${top + h}Z`
                    : `M${bx},${top}V${top + h - r}Q${bx},${top + h} ${bx + r},${top + h}H${bx + bw - r}Q${bx + bw},${top + h} ${bx + bw},${top + h - r}V${top}Z`;
                  return <path key={s.name} d={path} fill={SERIES[j]} className="insight-col"><title>{`${hints?.[i] ?? l}, ${s.name}: ${fmt(v, kind)}`}</title></path>;
                })}
              </g>
            );
          })}
        </g>
        {labels.map((l, i) => (step >= 40 || i % Math.ceil(40 / step) === 0
          ? <text key={'t' + i} x={padL + step * i + step / 2} y={height - 6} className="chart__tick" textAnchor="middle">{l}</text> : null))}
      </svg>
    </figure>
  );
}

// A share bar inside a table cell: the length is the share of the column's largest value
export function ShareBar({ v, max, kind, children }: { v: number; max: number; kind: Kind; children?: ReactNode }) {
  const w = max > 0 ? Math.max(0, Math.min(100, (Math.abs(v) / max) * 100)) : 0;
  return (
    <span className="insight-share" title={fmt(v, kind)}>
      <span className="insight-share__bar" style={{ width: `${w}%` }} data-neg={v < 0 ? '1' : undefined} />
      <span className="insight-share__v">{children ?? fmt(v, kind)}</span>
    </span>
  );
}

export const secs = (s: number | null | undefined) => fmt(s ?? null, 'sec');
